import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import client, { formatApiError } from "../lib/authApi";
import { toast } from "sonner";

export default function RedefinirSenha() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const valid = password.length >= 8 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!token) { toast.error("Link inválido. Solicite um novo."); return; }
    if (!valid) { toast.error("As senhas não conferem ou são muito curtas."); return; }
    setSubmitting(true);
    try {
      await client.post("/api/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/entrar"), 2200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto section-padding py-10" data-testid="redefinir-senha-page">
      <Link
        to="/entrar"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="redefinir-back"
      >
        <ArrowLeft className="w-4 h-4" /> Entrar
      </Link>

      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Recuperar acesso</p>
      <h1 className="font-display text-4xl text-navy mt-2 leading-tight">Defina sua nova senha</h1>

      {!token && (
        <div
          className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center"
          data-testid="redefinir-no-token"
        >
          <p className="text-sm text-amber-900 leading-relaxed">Este link está inválido ou incompleto.</p>
          <Link
            to="/esqueci-senha"
            className="inline-block mt-4 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold"
            data-testid="redefinir-request-new"
          >
            Solicitar novo link →
          </Link>
        </div>
      )}

      {token && !done && (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="redefinir-form">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-navy/55">Nova senha</span>
            <div className="mt-2 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
              <input
                type={show ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                data-testid="redefinir-password"
                className="w-full pl-10 pr-10 py-3 rounded-xl border border-border bg-white text-navy/90 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                data-testid="redefinir-toggle-show"
                aria-label={show ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-navy/55">Confirmar senha</span>
            <div className="mt-2 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
              <input
                type={show ? "text" : "password"}
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repita a nova senha"
                data-testid="redefinir-confirm"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-border bg-white text-navy/90 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>
            {confirm && password !== confirm && (
              <p className="text-[11px] text-red-600 mt-1.5">As senhas não conferem.</p>
            )}
          </label>

          <button
            type="submit"
            disabled={submitting || !valid}
            data-testid="redefinir-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            {submitting ? "Salvando..." : "Definir nova senha"}
          </button>
        </form>
      )}

      {done && (
        <div
          className="mt-8 bg-[hsl(var(--parchment))] border border-gold/40 rounded-2xl p-6 text-center"
          data-testid="redefinir-success"
        >
          <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-3" />
          <p className="font-display text-xl text-navy">Senha redefinida!</p>
          <p className="text-sm text-navy/65 mt-2">Redirecionando para o login…</p>
        </div>
      )}
    </div>
  );
}
